'use client';

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import Image from 'next/image';
import { createPortal } from 'react-dom';
import logo from '@/assets/leadpops-mortgage-marketing-logo.webp';
import { sfx, soundEnabled, setSoundEnabled } from './gm-sound';

/**
 * Game layer for the demo-request concept ("build your lead engine").
 *
 * PRESENTATION ONLY. The form hands this file plain values (the visitor's own answers and a couple of booleans);
 * everything here — XP, levels, streaks, badges, the map, the player card, sound, keyboard shortcuts — is derived from
 * those values and never writes back into the form. The only interaction with the form is the letter-key shortcut,
 * which simply clicks the answer button the visitor could have clicked (the form's own handler runs).
 */

/* ── data ─────────────────────────────────────────────────────────────────────────────────────── */
export type GMAnswers = {
  role?: string; volume?: string; team?: string; years?: string; budget?: string; loanSize?: string;
  situation?: string; sources?: string[]; agency?: string; crm?: string; website?: string; performance?: string; siteUrl?: string;
  agents?: string; importance?: string; challenge?: string; approach?: string; timeline?: string;
  primaryState?: string; extraStates?: string[];
  nl?: { identity?: string; intent?: string; timeline?: string; challenge?: string };
};
export type GMState = {
  stepId: string;
  licensed: boolean | null;
  progress: number;
  questionsLeft: number;
  a: GMAnswers;
  profile: { name: string; company?: string; verified: boolean } | null;
  contact: { valid: number; total: number };
  firstName: string;
  submitting: boolean;
  /** the visitor's own draft id + NMLS number: lets the game layer notice when the lookup lands (read-only, see useEarlyProfile) */
  draftId?: string | null;
  nmls?: string;
};

const SCALE = {
  volume: ['0-1 loans', '2-3 loans', '4-5 loans', '6-10 loans', '11-20 loans', '21-50 loans', '50+ loans'],
  team: ['Just me', '2-3 people', '4-6 people', '7-10 people', '11-20 people', '20+ people'],
  years: ['Less than 1 year', '1-3 years', '4-7 years', '8-15 years', '15+ years'],
  budget: ['$100 or less', '$101 - $250', '$251 - $500', '$501 - $1,000', '$1,001 - $2,000', '$2,001 - $3,000', '$3,001 - $5,000', '$5,001 - $10,000', '$10,001 - $20,000', '$20,001 - $30,000', '$30,001 - $50,000', 'Over $50,000'],
  loan: ['Under $150k', '$150k - $250k', '$250k - $400k', '$400k - $600k', '$600k - $800k', '$800k - $1M', 'Over $1M'],
};
const scalePct = (list: string[], v?: string) => { const i = v ? list.indexOf(v) : -1; return i < 0 ? 0 : Math.round(((i + 1) / list.length) * 100); };

const ABBR: Record<string, string> = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA', Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', 'District of Columbia': 'DC',
  Florida: 'FL', Georgia: 'GA', Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL', Indiana: 'IN', Iowa: 'IA', Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA', Maine: 'ME',
  Maryland: 'MD', Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN', Mississippi: 'MS', Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH', Oklahoma: 'OK',
  Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC', 'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT', Vermont: 'VT',
  Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI', Wyoming: 'WY',
};
/** tile-grid map: [col,row] on an 11x8 grid */
const GRID: Record<string, [number, number]> = {
  AK: [0, 0], ME: [10, 0], VT: [9, 1], NH: [10, 1],
  WA: [0, 2], ID: [1, 2], MT: [2, 2], ND: [3, 2], MN: [4, 2], IL: [5, 2], WI: [6, 2], MI: [7, 2], NY: [8, 2], RI: [9, 2], MA: [10, 2],
  OR: [0, 3], NV: [1, 3], WY: [2, 3], SD: [3, 3], IA: [4, 3], IN: [5, 3], OH: [6, 3], PA: [7, 3], NJ: [8, 3], CT: [9, 3],
  CA: [0, 4], UT: [1, 4], CO: [2, 4], NE: [3, 4], MO: [4, 4], KY: [5, 4], WV: [6, 4], VA: [7, 4], MD: [8, 4], DE: [9, 4],
  AZ: [1, 5], NM: [2, 5], KS: [3, 5], AR: [4, 5], TN: [5, 5], NC: [6, 5], SC: [7, 5], DC: [8, 5],
  OK: [3, 6], LA: [4, 6], MS: [5, 6], AL: [6, 6], GA: [7, 6],
  HI: [0, 7], TX: [3, 7], FL: [8, 7],
};

type Level = { n: number; title: string };
const LV_LICENSED: { title: string; ids: string[] }[] = [
  { title: 'Who you are', ids: ['nmls-prequalification', 'nmls-early', 'nmls-retry', 'nmls-loading', 'role'] },
  { title: 'Your production', ids: ['volume', 'team', 'years-in-industry', 'budget', 'loan-size'] },
  { title: 'Your lead engine', ids: ['experience', 'sources', 'agency', 'crm', 'website-status', 'website-performance', 'website-url'] },
  { title: 'Your growth plan', ids: ['partnerships', 'agent-importance', 'challenge', 'lead-gen-approach', 'timeline', 'states'] },
  { title: 'Claim your plan', ids: ['nmls-confirm', 'contact', 'preparing-contact', 'submitting', 'thankyou'] },
];
const LV_UNLICENSED: { title: string; ids: string[] }[] = [
  { title: 'Who you are', ids: ['nmls-prequalification', 'non-licensed-identity', 'non-licensed-intent'] },
  { title: 'What you need', ids: ['non-licensed-timeline', 'non-licensed-challenge'] },
  { title: 'Claim your plan', ids: ['contact', 'preparing-contact', 'submitting', 'thankyou'] },
];
export function levelsFor(licensed: boolean | null) { return licensed === false ? LV_UNLICENSED : LV_LICENSED; }
export function levelFor(stepId: string, licensed: boolean | null): Level & { total: number } {
  const L = levelsFor(licensed);
  if (stepId === 'welcome') return { n: 0, title: 'Ready?', total: L.length };
  const idx = L.findIndex((l) => l.ids.includes(stepId));
  const i = idx < 0 ? 0 : idx;
  return { n: i + 1, title: L[i].title, total: L.length };
}

/* ── XP + badges (derived) ────────────────────────────────────────────────────────────────────── */
export function computeXp(s: GMState): number {
  const a = s.a; const nl = a.nl || {};
  const flags = [a.role, a.volume, a.team, a.years, a.budget, a.loanSize, a.situation, a.agency, a.crm, a.website, a.agents, a.challenge, a.approach, a.timeline,
    a.primaryState, nl.identity, nl.intent, nl.timeline, nl.challenge, a.sources && a.sources.length ? 'x' : '', a.performance, a.siteUrl, a.importance];
  return flags.filter(Boolean).length * 10 + (s.profile?.verified ? 40 : 0) + s.contact.valid * 10;
}
type BadgeDef = { id: string; name: string; hint: string; won: string; earned: (s: GMState) => boolean; icon: string };
const BADGES_LICENSED: BadgeDef[] = [
  { id: 'verified', name: 'Verified Pro', hint: 'Confirm your NMLS profile', won: 'Your NMLS profile is confirmed', earned: (s) => !!s.profile?.verified, icon: 'M12 2l2.4 2 3.1-.3 1 3 2.8 1.4-.7 3 1.6 2.6-2.2 2.2-.2 3.1-3 .9-1.8 2.5L12 20l-3-1.6-3-.9-.2-3.1L3.6 12l1.6-2.6-.7-3 2.8-1.4 1-3 3.1.3L12 2zm-1.2 12.2 5-5-1.1-1.1-3.9 3.9-1.7-1.7-1.1 1.1 2.8 2.8z' },
  { id: 'territory', name: 'Territory Set', hint: 'Pick your states', won: 'Your licensed state is on the map', earned: (s) => !!s.a.primaryState, icon: 'M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21zm0-8.5a3 3 0 1 1 0-6 3 3 0 0 1 0 6z' },
  { id: 'stack', name: 'Stack Mapped', hint: 'Tell us your CRM', won: 'Your CRM is mapped', earned: (s) => !!s.a.crm, icon: 'M12 3 3 8l9 5 9-5-9-5zm-7 8.6L12 16l7-4.4M5 15.6 12 20l7-4.4' },
  { id: 'budget', name: 'Budget Locked', hint: 'Set your monthly budget', won: 'Your monthly budget is locked in', earned: (s) => !!s.a.budget, icon: 'M12 3v18M16.5 7.5c-.9-1.2-2.5-1.8-4.5-1.8-2.5 0-4 1.1-4 2.8 0 4 9 2 9 6.2 0 1.8-1.7 3-4.5 3-2.2 0-3.9-.8-4.8-2.2' },
  { id: 'web', name: 'Web Presence', hint: 'Describe your website', won: 'Your website is described', earned: (s) => !!s.a.website, icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zm-9-9h18M12 3c2.5 2.6 3.7 5.6 3.7 9s-1.2 6.4-3.7 9c-2.5-2.6-3.7-5.6-3.7-9S9.5 5.6 12 3z' },
  { id: 'network', name: 'Partner Network', hint: 'Work with agent partners', won: 'You work with real estate agent partners', earned: (s) => s.a.agents === 'Yes', icon: 'M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm8 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM2.5 19c.4-3 2.7-4.6 5.5-4.6M21.5 19c-.4-3-2.7-4.6-5.5-4.6M8.5 19c.4-3.2 1.8-4.6 3.5-4.6s3.1 1.4 3.5 4.6' },
];
/* The unlicensed path is never asked about states, CRM, budget or a website, so the licensed badges could never be earned there.
 * These three are earned by the visitor's OWN answers on that path (one per question they are actually asked). */
const BADGES_UNLICENSED: BadgeDef[] = [
  { id: 'goal', name: 'Goal Set', hint: 'Say what you are looking for', won: 'You told us what you are looking for', earned: (s) => !!s.a.nl?.intent, icon: 'M5 21V4m0 0h11l-2 4 2 4H5' },
  { id: 'timeline', name: 'Timeline Set', hint: 'Tell us how soon', won: 'You told us how soon you need it', earned: (s) => !!s.a.nl?.timeline, icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zm0-13v5l3 2' },
  { id: 'challenge', name: 'Challenge Named', hint: 'Name your biggest challenge', won: 'You named your biggest challenge', earned: (s) => !!s.a.nl?.challenge, icon: 'M13 2 4 14h7l-1 8 9-12h-7l1-8z' },
];
const badgesFor = (licensed: boolean | null): BadgeDef[] => (licensed === false ? BADGES_UNLICENSED : BADGES_LICENSED);

/* ── the name, the moment the lookup lands ───────────────────────────────────────────────────────
 * The form only reads the lookup result on its confirmation screen (end of the flow). Measured over 548 successful lookups
 * (90 days): fastest 37.9s, median 52s, p90 57s. So this waits 35s, then asks the SAME endpoint the form itself uses
 * (/api/model-match-data, GET) every 4s until the result is there, and hands the name to the player card. It never feeds the
 * form: nothing here writes to form state. It accepts a result only when its NMLS number is the one currently typed
 * (a corrected number must not show the old person) and applies the form's own "real data" rules. */
const EARLY_START_MS = 35000; const EARLY_EVERY_MS = 4000; const EARLY_GIVE_UP_MS = 120000;
function useEarlyProfile(draftId: string | null | undefined, nmls: string | undefined, licensed: boolean | null): { name: string; company?: string } | null {
  const [p, setP] = useState<{ name: string; company?: string } | null>(null);
  useEffect(() => {
    setP(null);
    const want = (nmls || '').trim();
    if (!draftId || licensed === false || want.length < 4) return;
    let stop = false; let timer: number | undefined; const t0 = Date.now();
    const sandbox = typeof window !== 'undefined' && !!(window as unknown as { __dmSandboxStarts?: unknown }).__dmSandboxStarts; // the sandbox answers after 2.5s
    const tick = async () => {
      if (stop || Date.now() - t0 > EARLY_GIVE_UP_MS) return;
      try {
        const r = await fetch(`/api/model-match-data?draftId=${encodeURIComponent(draftId)}`, { cache: 'no-store' });
        const j = await r.json(); const d = j && j.success && j.found ? j.data : null;
        if (d && d.noMatch) return; // the form's own screen deals with "no match"
        if (d) {
          const full = String(d.fullName || '').trim(); const num = String(d.nmlsNumber || d.nmls || '').trim();
          const real = full.length >= 3 && full.toLowerCase() !== 'null' && !full.toLowerCase().includes('null null') && num.length >= 4 && num === want;
          if (real) { if (!stop) setP({ name: full, company: d.company ? String(d.company) : undefined }); return; }
        }
      } catch { /* network hiccup: try again */ }
      if (!stop) timer = window.setTimeout(tick, EARLY_EVERY_MS);
    };
    timer = window.setTimeout(tick, sandbox ? 3000 : EARLY_START_MS);
    return () => { stop = true; if (timer) window.clearTimeout(timer); };
  }, [draftId, nmls, licensed]);
  return p;
}

/* ── hooks ────────────────────────────────────────────────────────────────────────────────────── */
function useCount(target: number, ms = 600) {
  const [v, setV] = useState(target);
  const from = useRef(target);
  useEffect(() => {
    const start = performance.now(); const a = from.current; let raf = 0;
    const tick = (t: number) => {
      const k = Math.min(1, (t - start) / ms); const eased = 1 - Math.pow(1 - k, 3);
      setV(Math.round(a + (target - a) * eased)); if (k < 1) raf = requestAnimationFrame(tick); else from.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
}
/** How long after the last XP gain an answer still counts toward the streak. The streak tooltip reads this same number. */
export const STREAK_WINDOW_MS = 7000;
const reduced = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
/** a small gold burst that leaves the given element (a badge tile); falls back to the toast corner when the panel is not on screen (phones) */
const burstFrom = (el: Element | null) => {
  if (reduced()) return;
  const r = el ? el.getBoundingClientRect() : null;
  const origin = r && r.width > 0 ? { x: (r.left + r.width / 2) / window.innerWidth, y: (r.top + r.height / 2) / window.innerHeight } : { x: 0.88, y: 0.14 };
  confetti({ particleCount: 34, spread: 75, startVelocity: 30, gravity: 0.9, scalar: 0.85, origin, colors: ['#ffc93c', '#ff9d2e', '#ffe08a', '#ff34a7', '#22e4ff'], zIndex: 10050, ticks: 110 });
};
const burst = (n = 70) => {
  if (reduced()) return;
  confetti({ particleCount: n, spread: 80, startVelocity: 38, origin: { y: 0.28 }, colors: ['#ff34a7', '#294bfd', '#22e4ff', '#b6ff5c', '#ffc93c'], zIndex: 10050, ticks: 120 });
};

/** The saved theme. ONE source of truth: the site's `lp-theme` ('light' | 'dark', written by the site toggle and by this form's own toggle). Only when lp-theme is not set at all does an older form-only choice (`gm_theme`) count; otherwise dark. */
const gmStoredLight = (): boolean => {
  try { const l = localStorage.getItem('lp-theme'); if (l === 'light' || l === 'dark') return l === 'light'; return localStorage.getItem('gm_theme') === 'light'; } catch { return false; }
};
/** keep the root layout's html[data-skin-mode] in step with the form's .gm-light class (the layout only knows lp-theme, so a legacy gm_theme fallback would otherwise split them) */
const setSkinMode = (light: boolean) => { try { document.documentElement.setAttribute('data-skin-mode', light ? 'light' : 'dark'); } catch { /* ignore */ } };

/** First child of every game root: applies a saved LIGHT choice while the HTML is parsed, before first paint. The server always renders dark (the default), and the saved theme was only read after hydration, so light visitors would see a dark flash. */
export function GMThemeBoot() {
  // a root created on the client (the thank-you swaps its loading screen for a new root) never runs the inline script; a layout effect runs before paint there
  useLayoutEffect(() => { const l = gmStoredLight(); document.querySelectorAll('.gm-root').forEach((r) => r.classList.toggle('gm-light', l)); setSkinMode(l); }, []);
  return <script dangerouslySetInnerHTML={{ __html: "(function(){try{var t=localStorage.getItem('lp-theme');var l=t==='light'||t==='dark'?t==='light':localStorage.getItem('gm_theme')==='light';document.documentElement.setAttribute('data-skin-mode',l?'light':'dark');if(l){var s=document.currentScript;var r=s&&s.parentElement;if(r)r.classList.add('gm-light');}}catch(e){}})();" }} />;
}

/** The brand mark, linking back to the site. On a white pill so its dark "by rebel iQ" text reads on the dark theme too. */
export function GMLogo({ className = '' }: { className?: string }) {
  return (
    <a href="/" className={`gm-logo ${className}`.trim()} aria-label="LeadPops by rebel iQ — back to the site">
      <Image src={logo} alt="LeadPops by rebel iQ" priority />
    </a>
  );
}

/* ── backdrop: perspective neon floor + stars ─────────────────────────────────────────────────── */
export function GMBackdrop() {
  const stars = useMemo(() => Array.from({ length: 46 }, (_, i) => ({ l: (i * 37) % 100, t: (i * 53) % 62, d: (i * 0.37) % 5, s: 1 + (i % 3) })), []);
  return (
    <div className="gm-backdrop" aria-hidden="true">
      <div className="gm-sky" />
      {stars.map((s, i) => <i key={i} className="gm-star" style={{ left: `${s.l}%`, top: `${s.t}%`, width: s.s, height: s.s, animationDelay: `${s.d}s` }} />)}
      <div className="gm-sun" />
      <div className="gm-floor"><div className="gm-floor-grid" /></div>
    </div>
  );
}

/* ── click sounds: every interactive control, one delegated listener ───────────────────────────── */
export function useClickSounds() {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!soundEnabled()) return;
      const t = e.target as HTMLElement | null;
      if (!t || !t.closest) return;
      if (!t.closest('.gm-root, .p-dropdown-panel, .p-multiselect-panel')) return; // PrimeReact panels portal to <body>
      if (t.closest('.gm-snd, .gm-theme')) return; // the toggles have their own feedback
      const cb = t.closest('input[type="checkbox"]') as HTMLInputElement | null;
      if (cb) { if (cb.checked) sfx.on(); else sfx.off(); return; }
      const label = t.closest('label');
      if (label && label.querySelector('input[type="checkbox"]')) return; // the label forwards a click to the checkbox: that one sounds
      const msItem = t.closest('.p-multiselect-item');
      if (msItem) { if (msItem.classList.contains('p-highlight')) sfx.off(); else sfx.on(); return; }
      if (t.closest('.p-dropdown-item')) { sfx.tap(); return; }
      if (t.closest('.p-dropdown, .p-multiselect')) { sfx.open(); return; }
      const opt = t.closest('[data-dm-option]');
      if (opt) { if (opt.querySelector('.gm-dot.sq')) { if (opt.classList.contains('is-on')) sfx.off(); else sfx.on(); } else sfx.tap(); return; }
      const btn = t.closest('button, a, [role="button"]');
      if (btn) { if (btn.matches('.gm-btn-primary, .btn-primary')) sfx.cta(); else if (btn.matches('.gm-btn-quiet')) sfx.back(); else sfx.tap(); }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);
}
/** thin wrapper so pages that are not wrapped in <GMGame> (the thank-you page) get the same sounds */
export function GMClicks() { useClickSounds(); return null; }

/* ── light / dark toggle (lower right) ────────────────────────────────────────────────────────── */
export function GMThemeToggle() {
  const [light, setLight] = useState(false); // dark is the default; a stored 'light' choice wins
  const [ready, setReady] = useState(false); // the saved choice has been read: until then the class is left exactly as the pre-paint script set it
  useEffect(() => { setLight(gmStoredLight()); setReady(true); }, []);
  // a change made in another tab (the site's toggle, or this form open elsewhere) updates this page live
  useEffect(() => {
    const onStorage = (e: StorageEvent) => { if (e.key === null || e.key === 'lp-theme' || e.key === 'gm_theme') setLight(gmStoredLight()); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  // the recurring CTA cue (an animation in gm.css) pauses while the tab is hidden
  useEffect(() => {
    const sync = () => document.querySelectorAll('.gm-root').forEach((r) => r.classList.toggle('gm-paused', document.hidden));
    document.addEventListener('visibilitychange', sync); sync();
    return () => document.removeEventListener('visibilitychange', sync);
  }, []);
  useEffect(() => { if (ready) { document.querySelector('.gm-root')?.classList.toggle('gm-light', light); setSkinMode(light); } }, [light, ready]);
  const toggle = () => { const n = !light; setLight(n); setSkinMode(n); try { localStorage.setItem('lp-theme', n ? 'light' : 'dark'); localStorage.setItem('gm_theme', n ? 'light' : 'dark'); } catch { /* ignore */ } };
  return (
    <button type="button" className="gm-theme" onClick={toggle} aria-pressed={light} aria-label={light ? 'Switch to the dark version' : 'Switch to the light version'} title={light ? 'Dark version' : 'Light version'}>
      {light ? (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>
      ) : (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
      )}
      <span>{light ? 'Dark' : 'Light'}</span>
    </button>
  );
}

/* ── the game layer ───────────────────────────────────────────────────────────────────────────── */
type Toast = { id: number; kind: 'badge' | 'xp'; text: string; sub?: string };

export function GMGame({ s, children }: { s: GMState; children: React.ReactNode }) {
  const lvl = levelFor(s.stepId, s.licensed);
  const xp = computeXp(s);
  const shownXp = useCount(xp);
  const badgeSet = badgesFor(s.licensed);
  const earned = badgeSet.filter((b) => b.earned(s)).map((b) => b.id);
  const earlyRaw = useEarlyProfile(s.draftId, s.nmls, s.licensed);
  const early = s.stepId === 'nmls-retry' || s.stepId === 'nmls-early' ? null : earlyRaw; // "not me" / editing the number: don't keep showing the old person
  const shownName = (s.profile && s.profile.name) || (early && early.name) || '';
  const prevShownName = useRef('');
  useEffect(() => { if (shownName && !prevShownName.current) sfx.pick(); prevShownName.current = shownName; }, [shownName]); // "found you"
  const shownProfile = s.profile || (early ? { name: early.name, company: early.company, verified: false } : null);

  const [sound, setSound] = useState(false);
  const [streak, setStreak] = useState(0);
  const [banner, setBanner] = useState<Level | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [pops, setPops] = useState<{ id: number; n: number }[]>([]);
  const prevXp = useRef(xp); const lastAt = useRef(0); const bestStreak = useRef(0);
  const maxLevel = useRef(0); const prevBadges = useRef<string[]>(earned); const idc = useRef(1);
  const startedAt = useRef(Date.now());

  useEffect(() => { setSound(soundEnabled()); }, []);
  useClickSounds();

  // XP gained -> pop, streak, sound
  useEffect(() => {
    if (xp > prevXp.current) {
      const delta = xp - prevXp.current; const now = Date.now();
      const fast = lastAt.current && now - lastAt.current < STREAK_WINDOW_MS;
      const next = fast ? streak + 1 : 1; setStreak(next); bestStreak.current = Math.max(bestStreak.current, next); lastAt.current = now;
      const id = idc.current++; setPops((p) => [...p, { id, n: delta }]);
      window.setTimeout(() => setPops((p) => p.filter((x) => x.id !== id)), 1500); // after the 1.4s CSS animation has finished
      if (next >= 2) sfx.streak(next);
      try { navigator.vibrate?.(8); } catch { /* ignore */ }
    }
    prevXp.current = xp;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xp]);

  // level up banner
  useEffect(() => {
    if (lvl.n > maxLevel.current) {
      maxLevel.current = lvl.n;
      if (lvl.n >= 1) {
        // the unlicensed path is only four questions: a full-screen banner + confetti at every level is noise there, so only the final one gets it
        const quiet = s.licensed === false && lvl.n !== lvl.total;
        sfx.level();
        if (!quiet) {
          setBanner({ n: lvl.n, title: lvl.title });
          if (lvl.n >= 2) burst(lvl.n === lvl.total ? 130 : 60);
          const t = window.setTimeout(() => setBanner(null), 1900); // after the 1.8s CSS animation has finished
          return () => window.clearTimeout(t);
        }
      }
    }
  }, [lvl.n, lvl.title, lvl.total]);

  // badge unlocks
  useEffect(() => {
    const fresh = earned.filter((id) => !prevBadges.current.includes(id));
    if (fresh.length) {
      fresh.forEach((id) => {
        const b = badgeSet.find((x) => x.id === id)!; const tid = idc.current++;
        setToasts((t) => [...t, { id: tid, kind: 'badge', text: b.name, sub: 'Badge unlocked' }]);
        window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== tid)), 2800); // after the 2.6s CSS animation has finished
      });
      sfx.badge();
    }
    prevBadges.current = earned;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earned.join('|')]);

  // remember the run for the thank-you page (decorative recap)
  useEffect(() => {
    try { sessionStorage.setItem('gm_run', JSON.stringify({ xp, badges: earned, streak: bestStreak.current, secs: Math.round((Date.now() - startedAt.current) / 1000) })); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xp, earned.join('|')]);

  // letter keys pick answers (A = first option ...): a shortcut that clicks the same button the visitor could click
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement as HTMLElement | null;
      if (el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable)) return;
      if (document.querySelector('.p-dropdown-panel, .p-multiselect-panel')) return;
      if (e.key === 'Enter' && s.stepId === 'welcome') { (document.querySelector('.gm-root .btn-primary') as HTMLElement | null)?.click(); return; }
      if (/^[a-z]$/i.test(e.key)) {
        const btns = Array.from(document.querySelectorAll<HTMLElement>('.gm-root [data-dm-option]'));
        const b = btns[e.key.toLowerCase().charCodeAt(0) - 97];
        if (b) { e.preventDefault(); b.click(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [s.stepId]);

  const toggleSound = () => { const on = !sound; setSound(on); setSoundEnabled(on); if (on) sfx.badge(); };
  const pct = Math.round(Math.max(s.stepId === 'welcome' ? 0.05 : 0, s.progress) * 100);
  const L = levelsFor(s.licensed);

  return (
    <div className="gm-root lp-root">
      <GMThemeBoot />
      <GMBackdrop />

      <header className="gm-hud">
        <div className="gm-hud-in">
          <GMLogo />
          <HudTip id="gm-tip-level" className="gm-lvl" icon="M4 20V10m6 10V4m6 16v-7m6 7H2" title="Levels" pill={lvl.n === 0 ? 'Start' : lvl.n === lvl.total ? 'Final level' : `Level ${lvl.n} of ${lvl.total}`}
            text={lvl.n === 0 ? `The form is ${lvl.total} short levels: ${L.map((l) => l.title).join(', ')}. Start below to begin level 1.` : `The form is ${lvl.total} short levels: ${L.map((l) => l.title).join(', ')}. You're on “${lvl.title}”. Finish the last one and your session is locked in.`}>
            <span className="gm-lvl-n">{lvl.n === lvl.total ? 'BOSS LEVEL' : lvl.n === 0 ? 'START' : `LEVEL ${lvl.n}/${lvl.total}`}</span>
            <span className="gm-lvl-t">{lvl.title}</span>
          </HudTip>
          <div className="gm-xpbar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="Plan progress">
            <div className="gm-xpfill" style={{ width: `${pct}%` }} />
            {L.slice(0, -1).map((_, i) => <b key={i} className="gm-tick" style={{ left: `${((i + 1) / L.length) * 100}%` }} />)}
            <span className="gm-xppct">{pct}%</span>
          </div>
          <div className="gm-hud-r">
            {s.questionsLeft > 0 && s.stepId !== 'welcome' && <span className="gm-left">{s.questionsLeft} left</span>}
            {streak >= 2 && <HudTip id="gm-tip-streak" icon="M13 2 4 14h7l-1 8 9-12h-7l1-8z" title="Streak" pill={`×${streak}`} text={`Answer within ${STREAK_WINDOW_MS / 1000} seconds of the last question to keep your streak going. Pure bragging rights: it doesn't change your plan.`}><motion.span key={streak} initial={{ scale: 1.5 }} animate={{ scale: 1 }} className="gm-streak">🔥 ×{streak}</motion.span></HudTip>}
            <span className="gm-xp"><HudTip id="gm-tip-xp" icon="M12 3l2.6 5.6 6.1.7-4.5 4.2 1.2 6L12 16.5 6.6 19.5l1.2-6L3.3 9.3l6.1-.7L12 3z" title="XP" pill={`${shownXp} XP`} text={s.licensed === false ? 'Your progress score: 10 XP for every answer and every contact detail. It’s a game, but the answers are real: they’re what we use to tailor your plan.' : 'Your progress score: 10 XP for every answer, and +40 for confirming your NMLS profile. It’s a game, but the answers are real: they’re what we use to tailor your plan.'}><b>{shownXp}</b> XP</HudTip><span className="gm-xp-pops" aria-hidden="true">{pops.map((p) => <i key={p.id} className="gm-pop">+{p.n} XP</i>)}</span></span>
            <button type="button" className="gm-snd" onClick={toggleSound} aria-pressed={sound} aria-label={sound ? 'Sound on' : 'Sound off'} title={sound ? 'Sound on' : 'Sound off'}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H3v6h3l5 4V5z" />{sound ? <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" /> : <path d="m17 9 5 6m0-6-5 6" />}</svg>
            </button>
          </div>
        </div>
        {banner && (
          <div key={banner.n} className="gm-banner" role="status">
            <span className="gm-banner-k">{banner.n === lvl.total ? 'FINAL LEVEL' : `LEVEL ${banner.n} UNLOCKED`}</span>
            <span className="gm-banner-t">{banner.title}</span>
          </div>
        )}
      </header>

      <div className={`gm-stage${s.stepId === 'welcome' ? ' is-welcome' : ''}`}>
        <div className="gm-card-wrap">{children}</div>
        <aside className="gm-side" aria-label="Your progress">
          <PlayerCard s={{ ...s, profile: shownProfile }} lvl={lvl} />
          {s.licensed !== false && <Territory a={s.a} />}
          <BadgeRow s={s} />
        </aside>
      </div>

      <GMThemeToggle />
      <div className="gm-toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className="gm-toast">
            <span className="gm-toast-ico">★</span><div><div className="gm-toast-sub">{t.sub}</div><div className="gm-toast-t">{t.text}</div></div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── side modules ─────────────────────────────────────────────────────────────────────────────── */
function initials(name: string) { const p = name.trim().split(/\s+/).filter(Boolean); return ((p[0]?.[0] || '') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase() || '★'; }

function Stat({ label, value, pct }: { label: string; value?: string; pct: number }) {
  return (
    <div className="gm-stat">
      <div className="gm-stat-h"><span>{label}</span><b>{value || '—'}</b></div>
      <div className="gm-stat-bar"><motion.i initial={false} animate={{ width: `${value ? Math.max(8, pct) : 0}%` }} transition={{ type: 'spring', stiffness: 120, damping: 20 }} /></div>
    </div>
  );
}

function BriefRow({ label, value }: { label: string; value?: string }) {
  return (
    <div className={`gm-brief-row${value ? ' on' : ''}`}>
      <i aria-hidden="true">{value ? '✓' : ''}</i>
      <div><span>{label}</span>{value ? <b key={value} className="gm-name-in">{value}</b> : <b className="gm-brief-empty">—</b>}</div>
    </div>
  );
}

function PlayerCard({ s, lvl }: { s: GMState; lvl: Level & { total: number } }) {
  const name = s.profile?.name || s.firstName || 'You';
  const a = s.a;
  return (
    <section className="gm-pcard">
      <div className="gm-pcard-h">
        <div className="gm-avatar" key={initials(s.profile?.name || s.firstName || '')}>{initials(s.profile?.name || s.firstName || '')}</div>
        <div className="gm-pcard-id">
          <div className="gm-pcard-name"><span key={name} className="gm-name-in">{name}</span>{s.profile?.verified && <span className="gm-vfy">✓ Verified</span>}</div>
          <div className="gm-pcard-sub">{[s.profile?.company, a.role || a.nl?.identity].filter(Boolean).join(' · ') || 'Player card'}</div>
        </div>
        <div className="gm-pcard-lv">{lvl.n === 0 ? '—' : lvl.n === lvl.total ? '★' : lvl.n}<small>LEVEL</small></div>
      </div>
      {s.licensed === false ? (
        <div className="gm-brief" aria-label="Your brief">
          <div className="gm-brief-k">Your brief</div>
          <BriefRow label="Looking for" value={a.nl?.intent} />
          <BriefRow label="Timeline" value={a.nl?.timeline} />
          <BriefRow label="Biggest challenge" value={a.nl?.challenge} />
        </div>
      ) : (
        <div className="gm-stats">
          <Stat label="Volume" value={a.volume} pct={scalePct(SCALE.volume, a.volume)} />
          <Stat label="Team" value={a.team} pct={scalePct(SCALE.team, a.team)} />
          <Stat label="Experience" value={a.years} pct={scalePct(SCALE.years, a.years)} />
          <Stat label="Budget" value={a.budget} pct={scalePct(SCALE.budget, a.budget)} />
          <Stat label="Avg loan" value={a.loanSize} pct={scalePct(SCALE.loan, a.loanSize)} />
        </div>
      )}
      {a.crm && <div className="gm-crm-row"><span>CRM</span><b>{a.crm}</b></div>}
    </section>
  );
}

function Territory({ a }: { a: GMAnswers }) {
  const primary = a.primaryState ? ABBR[a.primaryState] : undefined;
  const extra = new Set((a.extraStates || []).map((x) => ABBR[x]).filter(Boolean));
  const tiles = Object.entries(GRID);
  return (
    <section className="gm-terr" aria-label="Your territory">
      <div className="gm-terr-h"><span>Territory</span><b>{primary ? `${1 + extra.size} state${extra.size ? 's' : ''}` : '—'}</b></div>
      <div className="gm-map">
        {tiles.map(([ab, [c, r]]) => (
          <i key={ab} className={`gm-tile${ab === primary ? ' p' : extra.has(ab) ? ' x' : ''}`} style={{ gridColumn: c + 1, gridRow: r + 1 }}>{ab}</i>
        ))}
      </div>
    </section>
  );
}

type Tip = { id: string; x: number; y: number; below: boolean };
type CardProps = { id: string; tip: Tip; icon: string; title: string; pill: string; pillOn?: boolean; text: string };
/** The shared tooltip card: portalled to <body> (nothing clips it), dark slate, pill on the right, arrow to the target, clamped to the viewport. */
function TipCard({ id, tip, icon, title, pill, pillOn, text }: CardProps) {
  const W = 268; const left = Math.min(Math.max(tip.x, W / 2 + 10), (typeof window !== 'undefined' ? window.innerWidth : 1200) - W / 2 - 10);
  return createPortal(
    <div id={id} className={`gm-tip${tip.below ? ' below' : ''}${pillOn ? ' on' : ''}`} role="tooltip" style={{ left, top: tip.y, ['--tip-arrow' as string]: `${tip.x - left}px` }}>
      <div className="gm-tip-h">
        <span className="gm-tip-ico"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={icon} /></svg></span>
        <b>{title}</b>
        <em>{pill}</em>
      </div>
      <p>{text}</p>
    </div>,
    document.body,
  );
}

function BadgeTip({ b, on, tip }: { b: BadgeDef; on: boolean; tip: Tip }) {
  return <TipCard id={`gm-tip-${b.id}`} tip={tip} icon={b.icon} title={b.name} pill={on ? '✓ Earned' : 'Locked'} pillOn={on} text={on ? b.won : `To earn it: ${b.hint.charAt(0).toLowerCase()}${b.hint.slice(1)}.`} />;
}

/** Wraps a top-bar element (level chip, XP counter, streak) so hover / keyboard focus / tap explains it in the same card as the badges. */
export function HudTip({ id, icon, title, pill, text, children, className = '' }: { id: string; icon: string; title: string; pill: string; text: string; children: React.ReactNode; className?: string }) {
  const [tip, setTip] = useState<Tip | null>(null);
  const touch = useRef(false); // opened by a finger: no hover-out on a phone, so it stays until you tap elsewhere (or ~5 s)
  const open = (el: HTMLElement) => { const r = el.getBoundingClientRect(); setTip({ id, x: r.left + r.width / 2, y: r.bottom + 12, below: true }); };
  const leave = () => { if (!touch.current) setTip(null); };
  useEffect(() => {
    if (!tip) return;
    const off = () => setTip(null); const tap = (e: PointerEvent) => { if (!(e.target as HTMLElement).closest?.(`[data-hudtip="${id}"]`)) off(); };
    const timer = touch.current ? window.setTimeout(off, 5000) : undefined;
    window.addEventListener('scroll', off, true); window.addEventListener('resize', off); document.addEventListener('pointerdown', tap);
    return () => { if (timer) window.clearTimeout(timer); window.removeEventListener('scroll', off, true); window.removeEventListener('resize', off); document.removeEventListener('pointerdown', tap); };
  }, [tip, id]);
  return (
    <>
      <button type="button" data-hudtip={id} className={`gm-hud-tip ${className}`.trim()} aria-describedby={tip ? id : undefined}
        onPointerDown={(e) => { touch.current = e.pointerType === 'touch'; }} onMouseEnter={(e) => open(e.currentTarget)} onMouseLeave={leave} onFocus={(e) => open(e.currentTarget)} onBlur={leave} onClick={(e) => open(e.currentTarget)}>
        {children}
      </button>
      {tip && <TipCard id={id} tip={tip} icon={icon} title={title} pill={pill} text={text} />}
    </>
  );
}

function BadgeRow({ s }: { s: GMState }) {
  const set = badgesFor(s.licensed); const few = set.length <= 4; // three badges get a caption each; six stay a compact row
  const [tip, setTip] = useState<Tip | null>(null);
  // tiles that JUST flipped to earned play the unlock animation (never on first render, never for badges already earned)
  const [fresh, setFresh] = useState<string[]>([]);
  const seen = useRef<Set<string> | null>(null);
  const earnedKey = set.filter((b) => b.earned(s)).map((b) => b.id).join('|');
  useEffect(() => {
    const now = earnedKey ? earnedKey.split('|') : [];
    if (seen.current === null) { seen.current = new Set(now); return; }
    const added = now.filter((id) => !seen.current!.has(id));
    if (!added.length) return;
    added.forEach((id) => seen.current!.add(id));
    setFresh((f) => [...f, ...added]);
    burstFrom(document.querySelector(`[data-badge="${added[0]}"] .gm-badge`));
    const t = window.setTimeout(() => setFresh((f) => f.filter((id) => !added.includes(id))), 1300);
    return () => window.clearTimeout(t);
  }, [earnedKey]);
  const open = (id: string, el: HTMLElement) => { const r = el.getBoundingClientRect(); const below = r.top < 150; setTip({ id, x: r.left + r.width / 2, y: below ? r.bottom + 12 : r.top - 12, below }); };
  useEffect(() => {
    if (!tip) return;
    const off = () => setTip(null);
    const tap = (e: PointerEvent) => { if (!(e.target as HTMLElement).closest?.('.gm-badge-btn')) off(); };
    window.addEventListener('scroll', off, true); window.addEventListener('resize', off); document.addEventListener('pointerdown', tap);
    return () => { window.removeEventListener('scroll', off, true); window.removeEventListener('resize', off); document.removeEventListener('pointerdown', tap); };
  }, [tip]);
  const shown = tip ? set.find((b) => b.id === tip.id) : undefined;
  return (
    <section className={`gm-badges${few ? ' few' : ''}`} aria-label="Badges">
      {set.map((b) => {
        const on = b.earned(s);
        const btn = (
          <button type="button" className="gm-badge-btn" data-badge={b.id} aria-label={`${b.name}: ${on ? 'earned' : 'locked'}`} aria-describedby={tip?.id === b.id ? `gm-tip-${b.id}` : undefined}
            onMouseEnter={(e) => open(b.id, e.currentTarget)} onMouseLeave={() => setTip(null)} onFocus={(e) => open(b.id, e.currentTarget)} onBlur={() => setTip(null)} onClick={(e) => open(b.id, e.currentTarget)}>
            <div className={`gm-badge${on ? ' on' : ''}${fresh.includes(b.id) ? ' fresh' : ''}`}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={b.icon} /></svg>
            </div>
          </button>
        );
        return few ? <div key={b.id} className={`gm-badge-cap${on ? ' on' : ''}${fresh.includes(b.id) ? ' fresh' : ''}`}>{btn}<span>{b.name}</span></div> : <React.Fragment key={b.id}>{btn}</React.Fragment>;
      })}
      {tip && shown && <BadgeTip b={shown} on={shown.earned(s)} tip={tip} />}
    </section>
  );
}

/* ── inline pieces the form drops into its own screens ────────────────────────────────────────── */
export function GMJourney({ licensedStarts = 5 }: { licensedStarts?: number }) {
  const nodes = ['Who you are', 'Your production', 'Your lead engine', 'Your growth plan', 'Claim your plan'].slice(0, licensedStarts);
  return (
    <div className="gm-journey" aria-hidden="true">
      {nodes.map((n, i) => (
        <React.Fragment key={n}>
          <div className={`gm-jn${i === 0 ? ' on' : ''}`}><i>{i === nodes.length - 1 ? '★' : i + 1}</i><span>{n}</span></div>
          {i < nodes.length - 1 && <b className="gm-jl" />}
        </React.Fragment>
      ))}
    </div>
  );
}

export function GMBoss({ valid, total }: { valid: number; total: number }) {
  return (
    <div className="gm-boss" aria-hidden="true">
      <div className="gm-boss-k">FINAL LEVEL</div>
      <div className="gm-boss-bar">{Array.from({ length: total }).map((_, i) => <i key={i} className={i < valid ? 'on' : ''} />)}</div>
      <div className="gm-boss-n">{valid}/{total}</div>
    </div>
  );
}

const num = (v?: string) => { const m = (v || '').match(/(\d+(?:\.\d+)?)/); return m ? parseFloat(m[1]) : NaN; };
export function GMSplit({ purchase, refinance }: { purchase?: string; refinance?: string }) {
  const p = num(purchase); const r = num(refinance);
  if (isNaN(p) && isNaN(r)) return null;
  const pp = isNaN(p) ? Math.max(0, 100 - r) : p; const rr = isNaN(r) ? Math.max(0, 100 - p) : r;
  return (
    <div className="gm-split" aria-hidden="true">
      <div className="gm-split-bar"><i style={{ width: `${pp}%` }} /><b style={{ width: `${rr}%` }} /></div>
      <div className="gm-split-l"><span>Purchase {pp}%</span><span>Refinance {rr}%</span></div>
    </div>
  );
}

/* ── the unlock (submit) screen: a vault that opens ───────────────────────────────────────────── */
export function GMUnlock({ message, showSuccess = false }: { message: string; showSuccess?: boolean }) {
  return (
    <div className="gm-unlock">
      <div className={`gm-chest${showSuccess ? ' open' : ''}`} aria-hidden="true">
        <div className="gm-rays" />
        <svg viewBox="0 0 120 100" width="150" height="125">
          <defs><linearGradient id="gmc" x1="0" x2="1"><stop offset="0" stopColor="#ff34a7" /><stop offset="1" stopColor="#7c5cff" /></linearGradient></defs>
          <g className="gm-lid"><path d="M12 44V34a48 22 0 0 1 96 0v10z" fill="url(#gmc)" stroke="#fff" strokeOpacity=".45" strokeWidth="2" /><rect x="52" y="30" width="16" height="14" rx="3" fill="#ffc93c" /></g>
          <rect x="12" y="44" width="96" height="46" rx="6" fill="#171a2c" stroke="#fff" strokeOpacity=".35" strokeWidth="2" />
          <rect x="12" y="52" width="96" height="6" fill="url(#gmc)" opacity=".6" />
          <rect x="52" y="46" width="16" height="20" rx="3" fill="#ffc93c" />
        </svg>
      </div>
      <h3 className="gm-q" style={{ textAlign: 'center' }}>{message}</h3>
      <div className="gm-dots"><i /><i /><i /></div>
    </div>
  );
}

/** Spec for the CRM answer marks (shared with the V2 assets) */
export const CRM_LOGOS: Record<string, string> = {
  Aidium: '/images/integrations/aidium.svg', Bonzo: '/images/integrations/bonzo.svg', Jungo: '/images/integrations/jungo.png',
  'Lead Mailbox': '/images/integrations/lead-mailbox.png', LionDesk: '/images/integrations/liondesk.svg', Surefire: '/images/integrations/surefire.png',
  'Go High Level': '/images/integrations/highlevel.svg', HubSpot: '/images/integrations/hubspot.svg', Insellerate: '/images/integrations/insellerate.png',
  Relcu: '/images/integrations/relcu.svg', Salesforce: '/images/integrations/salesforce.svg', 'Total Expert': '/images/integrations/total-expert.svg',
  Velocify: '/images/integrations/velocify.png',
};
export const CRM_PLATE = new Set(['Surefire', 'LionDesk']);
export function CrmMark({ option }: { option: string }) {
  const logo = CRM_LOGOS[option];
  if (logo) {
    const img = <img className="gm-crm-logo" src={logo} alt="" aria-hidden="true" />;
    return CRM_PLATE.has(option) ? <span className="gm-crm-plate">{img}</span> : <span className="gm-crm-white">{img}</span>;
  }
  const none = option.toLowerCase().includes("don't use");
  return (
    <svg className="gm-crm-glyph" viewBox="0 0 32 32" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {none ? <><circle cx="16" cy="16" r="10.5" /><path d="M8.6 23.4 23.4 8.6" /></> : <><circle cx="16" cy="16" r="10.5" /><path d="M10.5 16h.01M16 16h.01M21.5 16h.01" strokeWidth="3.4" /></>}
    </svg>
  );
}

/* ── thank-you extras (their own hooks, so the forked page's logic region stays untouched) ────── */
/** `sample` (preview links only): with no real run in this browser, show a sample run so the page can be reviewed without filling the form */
export function GMRecap({ sample = false }: { sample?: boolean }) {
  const [r, setR] = useState<{ xp: number; badges: string[]; streak: number; secs: number } | null>(null);
  useEffect(() => {
    try {
      const x = JSON.parse(sessionStorage.getItem('gm_run') || 'null');
      if (x && typeof x.xp === 'number') { setR(x); sfx.unlock(); }
      else if (sample) { setR({ xp: 190, badges: ['a', 'b', 'c', 'd', 'e', 'f'], streak: 12, secs: 112 }); sfx.unlock(); }
    } catch { /* ignore */ }
  }, []);
  if (!r) return null;
  const mm = Math.floor(r.secs / 60); const ss = String(r.secs % 60).padStart(2, '0');
  return (
    <div className="gm-score" aria-label="Your run">
      <div><b>{r.xp}</b><span>XP earned</span></div>
      <div><b>{r.badges.length}</b><span>Badges</span></div>
      {r.streak >= 2 && <div><b>×{r.streak}</b><span>Best streak</span></div>}
      <div><b>{mm}:{ss}</b><span>Time</span></div>
    </div>
  );
}

export function GMShare() {
  const [msg, setMsg] = useState('');
  const url = 'https://leadpops.com/mortgage-lead-generation-demo';
  const text = 'I just built my LeadPops growth blueprint.';
  const share = async () => {
    try {
      if (navigator.share) { await navigator.share({ title: 'LeadPops', text, url }); return; }
      await navigator.clipboard.writeText(`${text} ${url}`); setMsg('Link copied');
      window.setTimeout(() => setMsg(''), 2200);
    } catch { /* cancelled */ }
  };
  return (
    <div className="gm-share">
      <button type="button" className="gm-btn gm-btn-ghost" onClick={share}>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7M16 6l-4-4-4 4M12 2v14" /></svg>
        {msg || 'Share this'}
      </button>
    </div>
  );
}
